import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FolderIcon } from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import PassageChip from "../Passages/PassageChip";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";

// An empty cell.
export const Dash = () => (
  <span className="text-slate-300" aria-hidden="true">
    —
  </span>
);

// The code under a question's name, unless the name is the code (a nameless question shows its code as the name).
export const codeUnderName = (question) => (question.name && question.code !== question.name ? question.code : null);

// "Code · Term · Objective" under a question's name.
export const questionMetaParts = (question) =>
  [codeUnderName(question), question.purpose?.name, question.objective?.name].filter(Boolean);

/**
 * The "Question" cell of the lists: the name (a link to `href`; `onOpen` runs on its click), the passage
 * chip (`onPassage(passage)`) and a muted line with the code, the term and the objective.
 */
export function QuestionSummary({ question, href, onOpen, onPassage }) {
  const parts = questionMetaParts(question);
  return (
    <div className="min-w-[12rem] max-w-[26rem] whitespace-normal">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link
          to={href}
          onClick={onOpen}
          className={`rounded font-medium text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
        >
          {question.name || question.code || "—"}
        </Link>
        <PassageChip passage={question.passage} onClick={onPassage ? () => onPassage(question.passage) : undefined} />
      </div>
      {parts.length > 0 && (
        <p className="mt-0.5 truncate text-xs text-slate-500" title={parts.join(" · ")}>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && " · "}
              <span className={index === 0 && codeUnderName(question) ? "font-mono" : undefined}>{part}</span>
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

// The question's subject; with `showFolder`, the folder (exam) it is in under it.
export function SubjectCell({ question, showFolder = false }) {
  const [t] = useTranslation();
  const subject = question.subject?.name;
  const exam = showFolder ? question.folder?.parent?.name : null;
  if (!subject && !exam) return <Dash />;
  return (
    <div className="min-w-0">
      <div className="text-slate-700">{subject || "—"}</div>
      {exam && (
        <div className="relative mt-0.5 flex max-w-[16rem] items-center gap-1 text-xs text-slate-500" title={exam}>
          <FolderIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="sr-only">{t("folders.label")}:</span>
          <span className="truncate">{exam}</span>
        </div>
      )}
    </div>
  );
}

// Up and down votes of a question, with the balance. (relative: the sr-only text is positioned absolutely;
// without it, inside a scrolling table it escapes the scroll box and widens the page.)
export function ReviewCounts({ question }) {
  const [t] = useTranslation();
  const up = question.up_count || 0;
  const down = question.down_count || 0;
  if (!up && !down) return <span className="text-sm text-slate-400">{t("questions_page.table.no_reviews")}</span>;
  const net = up - down;
  return (
    <div className="flex items-center gap-3">
      <span className="relative flex items-center gap-1 text-emerald-600" title={t("question_list.votes_up", { count: up })}>
        <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">{t("question_list.votes_up", { count: up })}</span>
        <span className="text-sm font-medium tabular-nums" aria-hidden="true">
          {up}
        </span>
      </span>
      <span className="relative flex items-center gap-1 text-red-600" title={t("question_list.votes_down", { count: down })}>
        <HandThumbDownIcon className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">{t("question_list.votes_down", { count: down })}</span>
        <span className="text-sm font-medium tabular-nums" aria-hidden="true">
          {down}
        </span>
      </span>
      {net !== 0 && (
        <span
          className={`rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ${
            net > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
          }`}
        >
          {net > 0 ? "+" : ""}
          {net}
        </span>
      )}
    </div>
  );
}
